/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useMemo, useState } from 'react';
import { useInjection } from 'services/ioc/ioc.react';
import { SessionVerificationStore } from 'stores/SessionVerificationStore';
import { ServiceTypes } from 'constants/ServiceTypes';
import { Checkbox } from '@trackman/react-ui';
import { observer } from 'mobx-react-lite';
import { History } from 'history';
import mappingHelpers from 'helpers/mappingHelpers';
import { CellInfo } from 'react-table';
import SessionTableFilters from 'components/sessionTable/SessionTableFilters';
import { Button, Column } from '@trackman/web-shared-components';
import { MeatBalls } from './MeatBalls/MeatBalls';
import { SearchResultsWrapper, TitleBar } from './styles/SearchResultsStyles';
import SimpleTable from '../../components/sessionTable/SimpleTable';
import LockedIcon from 'components/lockedIcon/LockedIcon';
import { LockedSessionModal } from './LockedSessionModal/LockedSessionModal';
import csvExport from 'helpers/csvExport';
export interface IProps {
  location: Location;
  match: any;
  history: History;
}

const ROWS_PER_PAGE = 10;

export const SearchResults: React.FC<IProps> = observer(({ match, history }) => {
  const sessionVerificationStore = useInjection<SessionVerificationStore>(ServiceTypes.SessionVerificationStore);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [lockModalVisible, setLockModalVisible] = useState(false);

  useEffect(() => {
    fetchData().then(() => {
      sessionVerificationStore.filterSessions();
    });

    return () => {
      sessionVerificationStore.setPage(1);
    };
  }, []);

  const fetchData = async () => {
    const { date, teamId } = match.params;
    const queries: { key: string; value: string }[] = [];

    location.search
      .replace('?', '')
      .split('&')
      .forEach((query) => {
        const [key, value] = query.split('=');

        queries.push({
          key: key,
          value: value,
        });
      });

    if (queries.some((query) => query.key === 'locked' && query.value === 'true')) {
      setLockModalVisible(true);
    }

    setIsLoading(true);
    await sessionVerificationStore.fetchSessions(date, teamId);
    setIsLoading(false);
  };

  const renderMenu = (cellInfo: CellInfo) => {
    const { isLocked } = cellInfo.row.values;

    if (isLocked) {
      return <LockedIcon />;
    }

    return <MeatBalls id={cellInfo.row.id} sessionId={cellInfo.value} history={history} />;
  };

  const renderVerified = (isVerified: boolean) => {
    return isVerified ? <Checkbox className='verified-checkbox' isChecked isDisabled label='' toggleCheck={() => {}} /> : null;
  };

  const cellRenderer = (cellInfo: CellInfo) => {
    return (
      <div className='table-cell' title={cellInfo.value}>
        {cellInfo.value}
      </div>
    );
  };

  const columns: Column[] = useMemo(
    () => [
      {
        Header: 'Time (Local)',
        accessor: 'localStartTime',
        width: 120,
        Cell: (cellInfo: any, column: any) => mappingHelpers.timeDisplay(cellInfo.value),
      },
      { Header: 'Venue', accessor: 'venue.name', Cell: cellRenderer },
      { Header: 'Field', accessor: 'field.name', Cell: cellRenderer },
      { Header: 'Home Team', accessor: 'homeTeam.name', Cell: cellRenderer },
      { Header: 'Level', accessor: 'level.name', Cell: cellRenderer, width: 100 },
      { Header: 'League', accessor: 'league.name', Cell: cellRenderer, width: 120 },
      { Header: 'Game Ref.', accessor: 'gameReference', Cell: cellRenderer, minWidth: 140 },
      { Header: 'BT Verified', accessor: 'isVerified', Cell: (cellInfo: any) => renderVerified(cellInfo.value), width: 75 },
      { Header: 'PT Verified', accessor: 'isPlayerTrackingVerified', Cell: (cellInfo: any) => renderVerified(cellInfo.value), width: 75 },
      { Header: '', accessor: 'id', width: 25, Cell: (cellInfo: any) => renderMenu(cellInfo) },
      { Header: '', accessor: 'isLocked', width: 0, Cell: () => {} },
    ],
    [sessionVerificationStore.sessions, sessionVerificationStore.page]
  );

  const data = useMemo(
    () =>
      sessionVerificationStore.sessions.slice(
        (sessionVerificationStore.page - 1) * ROWS_PER_PAGE,
        sessionVerificationStore.page * ROWS_PER_PAGE
      ),
    [sessionVerificationStore.sessions, sessionVerificationStore.page]
  );

  const exportCSVHandler = () => {
    csvExport.exportSessionsToCSV(sessionVerificationStore.sessions, `${match.params.date}-sessions`);
  };

  return (
    <div className='page'>
      <SearchResultsWrapper>
        <TitleBar>
          <h1>Search results</h1>
          <Button
            label='Export Results'
            intent='primary'
            onClick={exportCSVHandler}
            disabled={sessionVerificationStore.sessions.length === 0}
          />
        </TitleBar>
        <SimpleTable
          sessions={sessionVerificationStore.sessions}
          columns={columns}
          data={data}
          page={sessionVerificationStore.page}
          onChangePage={(newPage: number) => sessionVerificationStore.setPage(newPage)}
          filters={SessionTableFilters({ store: sessionVerificationStore })}
          onHeaderClick={(columnKey: string): void => {}}
          sort='asc'
          isLoading={isLoading}
          history={history}
        />
        <LockedSessionModal open={lockModalVisible} onClose={() => setLockModalVisible(false)} />
      </SearchResultsWrapper>
    </div>
  );
});
